import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteShell, { Reveal } from './SiteShell';
import { BannerCarousel, Rail, Stars, fmtReviewDate, GalleryTile, Lightbox, Avatar } from './SiteBits';
import { RegistrationsBand } from './SiteExtras';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';

const FALLBACK_SERVICES = [
  { name: 'House Maid', icon: '🧹' }, { name: 'Cook', icon: '🍳' },
  { name: 'Baby Care', icon: '👶' }, { name: 'Elderly Care', icon: '🧓' },
  { name: 'Housekeeping', icon: '🧽' }, { name: 'Laundry', icon: '🧺' },
];

const STEPS = [
  ['Tell us what you need', 'Pick a service and answer a few quick questions about your requirement.'],
  ['We shortlist workers', 'Our team hand-picks verified, KYC-checked candidates that match you.'],
  ['Interview & choose', 'Talk to the candidates and select the one you like best.'],
  ['Trial, then continue', 'Start with a trial period. Happy? Continue. Not happy? Change or cancel.'],
];


const FAQS = [
  ['How are workers verified?', 'Every professional completes an Aadhaar + PAN KYC check, which our admin team reviews and approves before they can be assigned to any customer.'],
  ['Can I meet the worker before hiring?', 'Yes. We shortlist multiple candidates and you interview them over a call before selecting one.'],
  ['What if I do not like the worker?', 'You can reject the shortlist and ask for more candidates, or change the worker after the trial period — your security deposit stays with us, no extra charge.'],
  ['Is the security deposit refundable?', 'Yes. If you cancel after the trial, we refund the deposit minus the per-day usage charge, settled to your bank account in 6–8 business days.'],
  ['Which areas do you serve?', 'We currently operate across Delhi NCR & Gurugram, and we are expanding to more cities.'],
];

export default function Landing() {
  const { isAuthed } = useAuth();
  const [s, setS] = useState({});
  const [services, setServices] = useState(FALLBACK_SERVICES);
  const [banners, setBanners] = useState([]);
  const [videos, setVideos] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [galleryTotal, setGalleryTotal] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [rvSummary, setRvSummary] = useState(null);
  const [shot, setShot] = useState(null);   // index into `gallery` for the viewer

  useEffect(() => {
    // The web api client already unwraps the { success, data } envelope.
    api.get('/settings/public').then((d) => setS(d || {})).catch(() => {});
    api.get('/catalog/categories').then((d) => {
      const cats = d?.categories || [];
      if (cats.length) setServices(cats.map((c) => ({ name: c.name, icon: c.icon || '🧹' })));
    }).catch(() => {});
    api.get('/banners?audience=landing').then((d) => setBanners(d?.banners || [])).catch(() => {});
    // Videos and photos get their own row, so they're fetched separately —
    // a single mixed page could return no videos at all and leave a row empty.
    api.get('/gallery?mediaType=video&limit=12').then((d) => {
      setVideos(d?.images || []);
      setGalleryTotal((t) => t + (d?.total || 0));
    }).catch(() => {});
    api.get('/gallery?mediaType=image&limit=12').then((d) => {
      setPhotos(d?.images || []);
      setGalleryTotal((t) => t + (d?.total || 0));
    }).catch(() => {});
    api.get('/reviews?limit=20').then((d) => {
      setReviews(d?.reviews || []);
      setRvSummary(d?.summary || null);
    }).catch(() => {});
  }, []);

  // One ordered list behind both rows, so the viewer pages across them.
  const media = [...videos, ...photos];

  const store = s.playStoreUrl || '';
  const appHref = isAuthed ? '/app' : '/login';
  const appLabel = isAuthed ? 'Open the app' : 'Book online now';

  return (
    <SiteShell settings={s}>
      {/* 1. HERO */}
      <section className="hero">
        <div className="wrap hero-in">
          <div className="hero-anim">
            <h1>Trusted home help,<br />verified &amp; ready.</h1>
            <p>
              Hire KYC-verified maids, cooks, baby &amp; elderly carers near you. Interview candidates,
              start with a trial, and continue only when you're happy.
            </p>
            <div className="hero-cta">
              {/* Primary action is now the web app itself. */}
              <Link className="btn btn-primary btn-lg" to={appHref}>{appLabel} →</Link>
              {store
                ? <a className="btn btn-ghost btn-lg" href={store} target="_blank" rel="noreferrer">▶ Get the App</a>
                : <a className="btn btn-ghost btn-lg" href="#download">▶ Get the App</a>}
            </div>
            <div className="hero-badges">
              <span className="pill">🛡️ Aadhaar verified</span>
              <span className="pill">🧪 Trial period</span>
              <span className="pill">🔁 Free replacement</span>
            </div>
          </div>

          <div className="hero-anim-2">
            <div className="banner-box">
              <BannerCarousel banners={banners} />
            </div>
          </div>
        </div>
      </section>

      {/* Registrations & certifications, directly under the banner */}
      <RegistrationsBand />

      {/* 2. SERVICES */}
      <section className="sec" id="services">
        <div className="wrap center">
          <Reveal>
            <p className="eyebrow">Our Services</p>
            <h2>Help for every corner of your home</h2>
            <p className="lead">Full-time, part-time or live-in — choose what fits your family.</p>
          </Reveal>
          <div className="grid g3">
            {services.map((sv, i) => (
              <Reveal key={sv.name} delay={i * 0.06}>
                <div className="card" style={{ textAlign: 'left' }}>
                  <div className="ico">{sv.icon}</div>
                  <h3>{sv.name}</h3>
                  <p>Verified, background-checked professionals for {sv.name.toLowerCase()} — matched to your area and schedule.</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 3. HOW IT WORKS */}
      <section className="sec sec-alt" id="how">
        <div className="wrap">
          <Reveal>
            <p className="eyebrow">How it works</p>
            <h2>Hire in four simple steps</h2>
            <p className="lead">No guesswork. You meet the people before they join your home.</p>
          </Reveal>
          <div className="grid g4">
            {STEPS.map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.08}>
                <div className="step">
                  <div className="step-n">{i + 1}</div>
                  <div>
                    <h3>{t}</h3>
                    <p>{d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 4. GALLERY — videos on top, photos below, both self-scrolling */}
      {media.length > 0 && (
        <section className="sec" id="gallery">
          <div className="wrap">
            <Reveal>
              <div className="sec-head">
                <div>
                  <p className="eyebrow">Gallery</p>
                  <h2>Our work, in pictures</h2>
                  <p className="lead">Clips and photos from the teams and homes we care for every day.</p>
                </div>
                <Link className="viewall" to="/gallery">View all{galleryTotal ? ` (${galleryTotal})` : ''} →</Link>
              </div>
            </Reveal>
            {videos.length > 0 && (
              <Reveal>
                <Rail rows={1} ariaLabel="Videos" autoScrollMs={4000}>
                  {videos.map((g) => (
                    <GalleryTile key={g._id} item={g} onOpen={() => setShot(media.indexOf(g))} />
                  ))}
                </Rail>
              </Reveal>
            )}

            {photos.length > 0 && (
              <Reveal delay={0.05}>
                <div style={{ marginTop: videos.length ? 16 : 0 }}>
                  <Rail rows={1} ariaLabel="Photos" autoScrollMs={4000}>
                    {photos.map((g) => (
                      <GalleryTile key={g._id} item={g} onOpen={() => setShot(media.indexOf(g))} />
                    ))}
                  </Rail>
                </div>
              </Reveal>
            )}
          </div>
        </section>
      )}

      {/* 5. GOOGLE REVIEWS */}
      {reviews.length > 0 && (
        <section className="sec sec-alt" id="reviews">
          <div className="wrap">
            <Reveal>
              <p className="eyebrow">Reviews</p>
              <div className="rv-head">
                <div className="rv-score">
                  <b>{(rvSummary?.rating || 0).toFixed(1)}</b>
                  <div>
                    <Stars value={rvSummary?.rating || 0} />
                    <div className="rv-meta">
                      {rvSummary?.count ? `${rvSummary.count} Google reviews` : 'Google reviews'}
                    </div>
                  </div>
                </div>
                {rvSummary?.url ? (
                  <a className="viewall" href={rvSummary.url} target="_blank" rel="noreferrer"
                     style={{ marginLeft: 'auto' }}>
                    See all on Google →
                  </a>
                ) : null}
              </div>
            </Reveal>
            <Reveal>
              <Rail rows={1} ariaLabel="Customer reviews">
                {reviews.map((r) => (
                  <article className="rv-card" key={r._id}>
                    <div className="rv-top">
                      <Avatar src={r.avatar} name={r.author} />
                      <div>
                        <div className="rv-name">{r.author}</div>
                        <div className="rv-date">{fmtReviewDate(r.reviewedAt)}</div>
                      </div>
                      {r.source === 'google' && <span className="rv-g">GOOGLE</span>}
                    </div>
                    <Stars value={r.rating} />
                    {r.text ? <p className="rv-text">{r.text}</p> : null}
                  </article>
                ))}
              </Rail>
            </Reveal>
          </div>
        </section>
      )}

      {/* 6. STATS */}
      <section className="sec">
        <div className="wrap">
          <Reveal>
            <div className="stats">
              {[['100%', 'KYC verified staff'], ['24×7', 'Support & placement'], ['6–8 days', 'Refund settlement'], ['NCR', 'Delhi & Gurugram']].map(([b, l]) => (
                <div className="stat" key={l}><b>{b}</b><span>{l}</span></div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* 7. DOWNLOAD / BOOK */}
      <section className="sec sec-alt" id="download">
        <div className="wrap">
          <Reveal>
            <div className="cta">
              <h2>Book in your browser or on the app</h2>
              <p>Book help, interview candidates and manage payments — on the web or right from your phone.</p>
              <div className="cta-row">
                <Link className="btn btn-primary btn-lg" to={appHref}>{appLabel} →</Link>
                {store
                  ? <a className="btn btn-ghost btn-lg" href={store} target="_blank" rel="noreferrer">▶ Download on Google Play</a>
                  : <span className="pill">Coming soon on Google Play</span>}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 8. FAQ */}
      <section className="sec" id="faq">
        <div className="wrap" style={{ maxWidth: 820 }}>
          <Reveal>
            <p className="eyebrow">FAQ</p>
            <h2>Questions, answered</h2>
            <p className="lead">Everything you need to know before you hire.</p>
          </Reveal>
          {FAQS.map(([q, a], i) => (
            <Reveal key={q} delay={i * 0.04}>
              <details className="faq">
                <summary>{q}</summary>
                <p>{a}</p>
              </details>
            </Reveal>
          ))}
        </div>
      </section>
      <Lightbox items={media} index={shot} onClose={() => setShot(null)} onIndex={setShot} />
    </SiteShell>
  );
}
